import {
  definitionOfEarningsSpec,
  maximumAmountSpec,
  rateGuarantee,
} from "./sharedTypedAmount";

import type {
  TypedAmountSpec,
  TypedAmountTypeOption,
} from "../typedAmountTypes";

const NOT_INCLUDED: TypedAmountTypeOption = {
  value: "notIncluded",
  label: "Not Included",
  hideAmount: true,
};
const NOT_DISCLOSED: TypedAmountTypeOption = {
  value: "notDisclosed",
  label: "Not Disclosed",
  hideAmount: true,
};
const NOT_APPLICABLE: TypedAmountTypeOption = {
  value: "notApplicable",
  label: "Not Applicable",
  hideAmount: true,
};
const NOT_OFFERED: TypedAmountTypeOption = {
  value: "notOffered",
  label: "Not Offered",
  hideAmount: true,
};

const LIMITATION_OPTIONS: TypedAmountTypeOption[] = [
  { value: "years", label: "Years" },
  { value: "months", label: "Months" },
  { value: "noLimit", label: "No Limit", hideAmount: true },
  {
    value: "payableSameAsAnyOtherDisability",
    label: "Payable Same as Any Other Disability",
    hideAmount: true,
  },
  { value: "included", label: "Included", hideAmount: true },
  NOT_INCLUDED,
  NOT_DISCLOSED,
  NOT_OFFERED,
];

export const ltdTypedAmountFields: Record<string, TypedAmountSpec> = {
  maximumAmount: maximumAmountSpec,
  guaranteedIssue: {
    typeKey: "guaranteedIssueType",
    amountKey: "guaranteedIssueAmount",
    amountLabel: "Amount",
    typeOptions: [
      { value: "dollarAmount", label: "Dollar amount" },
      NOT_INCLUDED,
      NOT_DISCLOSED,
      NOT_OFFERED,
    ],
  },
  ownOccupationPeriod: {
    typeKey: "ownOccupationPeriodType",
    amountKey: "ownOccupationPeriodAmount",
    amountLabel: "Amount",
    typeOptions: [
      { value: "yearsOwnOccupation", label: "Years Own Occupation" },
      { value: "monthsOwnOccupation", label: "Months Own Occupation" },
      {
        value: "ownOccupationOnly",
        label: "Own Occupation Only",
        hideAmount: true,
      },
      NOT_INCLUDED,
      NOT_DISCLOSED,
      NOT_OFFERED,
    ],
  },
  gainfulEarningsTestOwnOccupation: {
    typeKey: "gainfulEarningsTestOwnOccupationType",
    amountKey: "gainfulEarningsTestOwnOccupationAmount",
    amountLabel: "Percentage",
    typeOptions: [
      { value: "percentage", label: "Percentage" },
      NOT_APPLICABLE,
      NOT_INCLUDED,
      NOT_DISCLOSED,
      NOT_OFFERED,
    ],
  },
  gainfulEarningsTestAnyOccupation: {
    typeKey: "gainfulEarningsTestAnyOccupationType",
    amountKey: "gainfulEarningsTestAnyOccupationAmount",
    amountLabel: "Percentage",
    typeOptions: [
      { value: "percentage", label: "Percentage" },
      NOT_APPLICABLE,
      NOT_INCLUDED,
      NOT_DISCLOSED,
      NOT_OFFERED,
    ],
  },
  eliminationPeriod: {
    typeKey: "eliminationPeriodType",
    amountKey: "eliminationPeriodDays",
    amountLabel: "Number of Days",
    typeOptions: [
      { value: "days", label: "Days" },
      NOT_INCLUDED,
      NOT_DISCLOSED,
      NOT_OFFERED,
    ],
  },
  benefitDuration: {
    typeKey: "benefitDurationType",
    amountKey: "benefitDurationAmount",
    amountLabel: "Amount",
    typeOptions: [
      { value: "ssnra", label: "SSNRA", hideAmount: true },
      { value: "ssnraWithRbd", label: "SSNRA with RBD", hideAmount: true },
      { value: "rbdWithSsnra", label: "RBD with SSNRA", hideAmount: true },
      {
        value: "adea1WithSsnra",
        label: "ADEA 1 with SSNRA",
        hideAmount: true,
      },
      { value: "adea1", label: "ADEA 1", hideAmount: true },
      { value: "adea2", label: "ADEA 2", hideAmount: true },
      { value: "adea3", label: "ADEA 3", hideAmount: true },
      { value: "adea4", label: "ADEA 4", hideAmount: true },
      { value: "twoYearAdea", label: "2 Year ADEA", hideAmount: true },
      { value: "threeYearAdea", label: "3 Year ADEA", hideAmount: true },
      { value: "fiveYearAdea", label: "5 Year ADEA", hideAmount: true },
      {
        value: "tenYearAdeaSchedule",
        label: "10 Year ADEA Schedule",
        hideAmount: true,
      },
      {
        value: "reducingBenefitDuration",
        label: "Reducing Benefit Duration",
        hideAmount: true,
      },
      { value: "ssAdea", label: "SS ADEA", hideAmount: true },
      { value: "toAge65", label: "To Age 65", hideAmount: true },
      { value: "toAge67", label: "To Age 67", hideAmount: true },
      { value: "laterOfAgeOrSsnra", label: "Later of Age or SSNRA" },
      {
        value: "lesserOfRbdOrNumberOfYears",
        label: "Lesser of RBD or Years",
      },
      { value: "yearDuration", label: "Years" },
      NOT_INCLUDED,
      NOT_DISCLOSED,
    ],
  },
  definitionOfEarnings: definitionOfEarningsSpec,
  otherLimitations: {
    typeKey: "otherLimitationsType",
    amountKey: "otherLimitationsAmount",
    amountLabel: "Timeframe",
    typeOptions: LIMITATION_OPTIONS,
  },
  mentalIllness: {
    typeKey: "mentalIllnessType",
    amountKey: "mentalIllnessAmount",
    amountLabel: "Timeframe",
    typeOptions: LIMITATION_OPTIONS,
  },
  substanceAbuse: {
    typeKey: "substanceAbuseType",
    amountKey: "substanceAbuseAmount",
    amountLabel: "Timeframe",
    typeOptions: LIMITATION_OPTIONS,
  },
  rateGuarantee,
  // Extended (non-v5) field. typeOptions mirror
  // shared/cellSpecs/dropdownCellSpecs.ts's minimumBenefitTypeValues, the
  // same set STD's own Minimum Benefit uses (CD-2552).
  minimumAmount: {
    typeKey: "minimumAmountType",
    amountKey: "minimumAmount",
    amountLabel: "Amount",
    typeOptions: [
      { value: "dollarAmount", label: "Dollar Amount" },
      {
        value: "percentageOfSalary",
        label: "Percentage of Salary",
        amountMax: 100,
      },
      NOT_DISCLOSED,
    ],
  },
};
